import { Link } from "@tanstack/react-router";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useAuth } from "@/hooks/use-auth";
import { ThemeToggle } from "@/components/ThemeToggle";
import {
  User as UserIcon,
  Settings as Cog,
  Users,
  HelpCircle,
  LogOut,
  BookOpen,
  Megaphone,
} from "lucide-react";
import { cn } from "@/lib/utils";

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  isStaff: boolean;
  isAdmin: boolean;
  onSignOut: () => void | Promise<void>;
}

export function MobileMoreSheet({ open, onOpenChange, isStaff, onSignOut }: Props) {
  const { profile } = useAuth();
  const initials = (profile?.full_name ?? profile?.email ?? "?").slice(0, 1).toUpperCase();

  const close = () => onOpenChange(false);

  const items: Array<{ to: string; label: string; icon: typeof UserIcon; show: boolean }> = [
    { to: "/app/settings", label: "Account & Settings", icon: Cog, show: true },
    { to: "/app/staff/users", label: "Users", icon: Users, show: isStaff },
    { to: "/app/staff/announcements", label: "Announcements", icon: Megaphone, show: isStaff },
    { to: "/app/help", label: "Help & docs", icon: BookOpen, show: true },
    { to: "/app/notifications", label: "Notification preferences", icon: HelpCircle, show: true },
  ];

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        className="rounded-t-3xl p-0 md:hidden max-h-[85vh] overflow-y-auto"
      >
        <SheetHeader className="px-5 pt-5 pb-3 text-left">
          <SheetTitle className="sr-only">More</SheetTitle>
          {profile && (
            <div className="flex items-center gap-3">
              <div className="grid h-12 w-12 shrink-0 place-items-center rounded-full bg-gradient-hero text-primary-foreground text-base font-bold">
                {profile.avatar_url ? (
                  <img
                    src={profile.avatar_url}
                    alt=""
                    className="h-full w-full rounded-full object-cover"
                  />
                ) : (
                  initials
                )}
              </div>
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-semibold">
                  {profile.full_name || profile.email}
                </div>
                {profile.email && profile.full_name && (
                  <div className="truncate text-xs text-muted-foreground">{profile.email}</div>
                )}
              </div>
            </div>
          )}
        </SheetHeader>

        <div className="px-3 pb-2">
          <ul className="space-y-1">
            {items
              .filter((i) => i.show)
              .map(({ to, label, icon: Icon }) => (
                <li key={to}>
                  <Link
                    to={to}
                    onClick={close}
                    className={cn(
                      "flex items-center gap-3 rounded-2xl px-3 py-3 text-sm font-medium",
                      "text-foreground hover:bg-muted active:bg-muted",
                    )}
                  >
                    <Icon className="h-5 w-5 text-muted-foreground" />
                    {label}
                  </Link>
                </li>
              ))}
          </ul>
        </div>

        <div className="border-t border-border/60 px-5 py-4">
          <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Theme
          </div>
          <ThemeToggle className="w-full justify-between" />
        </div>

        <div className="border-t border-border/60 px-3 py-3 pb-[max(env(safe-area-inset-bottom),0.75rem)]">
          <button
            type="button"
            onClick={async () => {
              await onSignOut();
              close();
            }}
            className="flex w-full items-center gap-3 rounded-2xl px-3 py-3 text-sm font-medium text-destructive hover:bg-destructive/10 active:bg-destructive/15"
          >
            <LogOut className="h-5 w-5" /> Sign out
          </button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
